import { useEffect, useMemo, useState } from 'react';
import { Save, RotateCcw, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth, AppRole } from '../auth/AuthContext';
import { usePermissions, PermAction } from '../auth/PermissionsContext';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';

const ROLES: AppRole[] = ['admin', 'direction', 'commercial', 'production', 'administratif', 'lecture'];
const ROLE_LABELS: Record<AppRole, string> = {
  admin: 'Administrateur',
  direction: 'Direction',
  commercial: 'Commercial',
  production: 'Production',
  administratif: 'Administratif',
  lecture: 'Lecture seule',
};

const MODULES: Array<{ key: string; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'clients', label: 'Clients' },
  { key: 'prestations', label: 'Prestations' },
  { key: 'artistes', label: 'Artistes' },
  { key: 'propositions', label: 'Propositions' },
  { key: 'devis', label: 'Devis' },
  { key: 'contrats', label: 'Contrats' },
  { key: 'facturation', label: 'Facturation' },
  { key: 'relances', label: 'Relances' },
  { key: 'agenda', label: 'Agenda' },
  { key: 'suivi', label: 'Suivi (artistes & domaines)' },
  { key: 'journal', label: "Journal d'audit" },
  { key: 'doublons', label: 'Doublons' },
  { key: 'corbeille', label: 'Corbeille' },
  { key: 'export_comptable', label: 'Export comptable' },
  { key: 'parametres', label: 'Paramètres' },
  { key: 'utilisateurs', label: 'Utilisateurs' },
];

const ACTIONS: PermAction[] = ['view', 'create', 'update', 'delete'];
const ACTION_LABELS: Record<PermAction, string> = {
  view: 'Voir',
  create: 'Créer',
  update: 'Modifier',
  delete: 'Supprimer',
};

type Key = `${AppRole}|${string}|${PermAction}`;

export default function RolesPermissions() {
  const { hasRole } = useAuth();
  const { matrix, refresh } = usePermissions();
  const [selectedRole, setSelectedRole] = useState<AppRole>('commercial');
  const [edits, setEdits] = useState<Record<Key, boolean>>({});
  const [saving, setSaving] = useState(false);

  const isAdmin = hasRole('admin');

  const baseMap = useMemo(() => {
    const m = new Map<Key, boolean>();
    matrix.forEach(r => m.set(`${r.role as AppRole}|${r.module}|${r.action}` as Key, r.allowed));
    return m;
  }, [matrix]);

  const get = (role: AppRole, module: string, action: PermAction): boolean => {
    const k = `${role}|${module}|${action}` as Key;
    if (k in edits) return edits[k];
    return baseMap.get(k) ?? false;
  };

  const toggle = (role: AppRole, module: string, action: PermAction) => {
    if (role === 'admin') return; // admin = tout, non modifiable
    const k = `${role}|${module}|${action}` as Key;
    setEdits(prev => ({ ...prev, [k]: !get(role, module, action) }));
  };

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    const rows = Object.entries(edits).map(([k, allowed]) => {
      const [role, module, action] = k.split('|') as [AppRole, string, PermAction];
      return { role, module, action, allowed };
    });
    const { error } = await supabase.from('role_permissions').upsert(rows, { onConflict: 'role,module,action' });
    setSaving(false);
    if (error) {
      toast.error('Erreur de sauvegarde : ' + translateError(error.message));
      return;
    }
    setEdits({});
    await refresh();
    toast.success(`${rows.length} permission(s) enregistrée(s)`);
  };

  const reset = () => setEdits({});

  // Bulk : tout cocher / décocher pour un module
  const toggleModuleAll = (module: string, value: boolean) => {
    if (selectedRole === 'admin') return;
    const newEdits = { ...edits };
    ACTIONS.forEach(a => {
      const k = `${selectedRole}|${module}|${a}` as Key;
      newEdits[k] = value;
    });
    setEdits(newEdits);
  };

  if (!isAdmin) {
    return (
      <div className="text-slate-400 text-sm bg-slate-900 border border-slate-800 rounded-xl p-6">
        Seuls les administrateurs peuvent gérer les rôles et permissions.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-amber-400 flex items-center gap-2">
            <ShieldCheck size={22} /> Rôles & permissions
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Définis précisément ce que chaque rôle peut voir, créer, modifier ou supprimer dans le CRM.
            Les administrateurs ont systématiquement tous les droits.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirtyCount > 0 && (
            <button onClick={reset} className="px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 flex items-center gap-2">
              <RotateCcw size={13} /> Annuler ({dirtyCount})
            </button>
          )}
          <button
            onClick={save}
            disabled={!dirtyCount || saving}
            className="px-4 py-2 text-xs bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-slate-950 font-medium flex items-center gap-2"
          >
            <Save size={13} /> {saving ? 'Enregistrement…' : `Enregistrer${dirtyCount ? ` (${dirtyCount})` : ''}`}
          </button>
        </div>
      </div>

      {/* Tabs rôles */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map(r => (
          <button
            key={r}
            onClick={() => setSelectedRole(r)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
              selectedRole === r ? 'bg-amber-500 text-slate-950 font-medium' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {ROLE_LABELS[r]}
            {r === 'admin' && <span className="ml-1.5 opacity-60">(verrouillé)</span>}
          </button>
        ))}
      </div>

      {/* Matrice du rôle sélectionné */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-800/60 text-xs uppercase text-slate-400">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Module</th>
              {ACTIONS.map(a => (
                <th key={a} className="px-4 py-3 font-medium text-center">{ACTION_LABELS[a]}</th>
              ))}
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {MODULES.map(mod => (
              <tr key={mod.key} className="hover:bg-slate-800/30">
                <td className="px-4 py-3 text-slate-200">{mod.label}</td>
                {ACTIONS.map(a => {
                  const value = get(selectedRole, mod.key, a);
                  const k = `${selectedRole}|${mod.key}|${a}` as Key;
                  const isDirty = k in edits;
                  return (
                    <td key={a} className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(selectedRole, mod.key, a)}
                        disabled={selectedRole === 'admin'}
                        className={`w-5 h-5 rounded border-2 transition-colors inline-flex items-center justify-center ${
                          value
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'bg-transparent border-slate-600 hover:border-slate-400'
                        } ${selectedRole === 'admin' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${isDirty ? 'ring-2 ring-amber-400/60' : ''}`}
                      >
                        {value && <span className="text-[11px] text-slate-950 leading-none">✓</span>}
                      </button>
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-right text-xs space-x-2">
                  <button
                    onClick={() => toggleModuleAll(mod.key, true)}
                    disabled={selectedRole === 'admin'}
                    className="text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
                  >Tout</button>
                  <button
                    onClick={() => toggleModuleAll(mod.key, false)}
                    disabled={selectedRole === 'admin'}
                    className="text-slate-400 hover:text-slate-200 disabled:opacity-40"
                  >Rien</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <div className="text-xs text-slate-500 bg-slate-900/50 border border-slate-800 rounded-lg p-4">
        💡 <strong>Astuce :</strong> les modifications sont en attente jusqu'à ce que tu cliques sur <em>Enregistrer</em>.
        Les cases avec un contour jaune sont en attente de sauvegarde.
        Les utilisateurs concernés verront les changements à leur prochain rafraîchissement de page.
      </div>
    </div>
  );
}
